import { useState } from "react";
import { PackageSearch } from "lucide-react";
import OrderTimeline from "@/components/OrderTimeline";
import { useLanguage } from "@/context/LanguageContext";
import { usePageMeta } from "@/hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TrackPage() {
  const { t } = useLanguage();
  usePageMeta(t.track.title + " | KAZIM'S", t.track.sub);
  const [query, setQuery] = useState("");
  const [order, setOrder] = useState(undefined);
  const [busy, setBusy] = useState(false);

  const lookup = async (e) => {
    e.preventDefault();
    const id = query.trim().toUpperCase();
    if (!id) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/orders/${encodeURIComponent(id)}`);
      const data = await res.json();
      setOrder(res.ok && data.ok ? data.order : null);
    } catch {
      setOrder(null);
    }
    setBusy(false);
  };

  return (
    <main className="pb-24 pt-32 md:pt-44">
      <div className="mx-auto max-w-2xl px-6 lg:px-10">
        <h1 className="font-serif text-4xl text-cream sm:text-5xl" data-testid="track-page-heading">
          {t.track.title}
        </h1>
        <p className="mt-4 text-base leading-relaxed text-cream/65">{t.track.sub}</p>

        <form onSubmit={lookup} className="mt-8 flex flex-col gap-3 sm:flex-row" data-testid="track-form">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t.track.placeholder}
            data-testid="track-order-id-input"
            className="w-full border hairline bg-white/[0.03] px-4 py-3.5 text-sm uppercase tracking-widest text-cream placeholder:text-cream/35 focus:border-gold/60"
          />
          <button
            type="submit"
            disabled={busy}
            data-testid="track-submit-button"
            className="bg-gold px-8 py-3.5 text-sm font-medium uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:bg-gold-light disabled:opacity-60"
          >
            {t.track.button}
          </button>
        </form>

        {order === null && (
          <p className="mt-6 border border-gold/40 bg-gold/10 px-4 py-3 text-sm text-gold-light" data-testid="track-not-found">
            {t.track.notFound}
          </p>
        )}

        {order && (
          <div className="mt-10 border hairline bg-velvet/40 p-7" data-testid="track-result-card">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b hairline pb-5">
              <div>
                <p className="text-[11px] uppercase tracking-luxe text-gold/90">{t.orderPage.orderLabel}</p>
                <p className="mt-1 font-serif text-xl tracking-[0.15em] text-cream" data-testid="track-result-order-id">
                  {order.order_id}
                </p>
              </div>
              <p className="font-serif text-2xl text-gold">₹{order.subtotal}</p>
            </div>
            {order.shiprocket?.awb_code && (
              <div className="mt-5 flex flex-wrap gap-x-8 gap-y-2 text-[13px] text-cream/65">
                <span>{t.orderPage.awb}: <span className="text-cream" data-testid="track-result-awb">{order.shiprocket.awb_code}</span></span>
                {order.shiprocket.courier_name && (
                  <span>{t.orderPage.courier}: <span className="text-cream" data-testid="track-result-courier">{order.shiprocket.courier_name}</span></span>
                )}
              </div>
            )}
            {!order.shiprocket?.awb_code && (
              <p className="mt-5 text-sm text-cream/55" data-testid="track-no-shipment">
                {t.orderPage.noTracking}
              </p>
            )}
            <div className="mt-7">
              <OrderTimeline order={order} />
            </div>
          </div>
        )}

        {order === undefined && (
          <div className="mt-12 flex items-start gap-4 border hairline bg-velvet/30 p-6 text-sm text-cream/50">
            <PackageSearch className="mt-0.5 h-5 w-5 shrink-0 text-gold/60" strokeWidth={1.5} />
            {t.track.sub}
          </div>
        )}
      </div>
    </main>
  );
}
